export { createBrowserImagePreviewWxApis, WEFAT_IMAGE_PREVIEW_API_NAMES, WxImagePreviewRuntimeError, } from './image-preview-adapter.js';
export { createPageDomWxApis, WEFAT_PAGE_DOM_API_NAMES, WxPageDomRuntimeError, } from './page-dom-adapter.js';
export { createBrowserRandomValuesWxApis, WEFAT_RANDOM_VALUES_API_NAMES, WxRandomValuesRuntimeError, } from './random-values-adapter.js';
export { createBrowserActionSheetWxApis, WEFAT_ACTION_SHEET_API_NAMES, WxActionSheetRuntimeError, } from './action-sheet-adapter.js';
export { createBrowserModalWxApis, WEFAT_MODAL_API_NAMES, WxModalRuntimeError, } from './modal-adapter.js';
export { createBrowserClipboardWxApis, WEFAT_CLIPBOARD_API_NAMES, WxClipboardRuntimeError, } from './clipboard-adapter.js';
export { createPageScrollWxApis, WEFAT_PAGE_SCROLL_API_NAMES, WxPageScrollRuntimeError, } from './page-scroll-adapter.js';
export { createNavigationWxApis, WEFAT_NAVIGATION_API_NAMES, WxNavigationRuntimeError, } from './navigation-adapter.js';
export { createPullDownRefreshWxApis, WEFAT_PULL_DOWN_REFRESH_API_NAMES, WxPullDownRefreshRuntimeError, } from './pull-down-refresh-adapter.js';
export { createBrowserRequestWxApis, WEFAT_REQUEST_API_NAMES, WxRequestRuntimeError, } from './request-adapter.js';
export { createBrowserThemeWxApis, WEFAT_THEME_API_NAMES, WxThemeRuntimeError, } from './theme-adapter.js';
export { createWebStorageWxApis, WEFAT_STORAGE_API_NAMES, WxStorageRuntimeError, } from './storage-adapter.js';
export { createBrowserSystemInfoWxApis, WEFAT_SYSTEM_INFO_API_NAMES, WxSystemInfoRuntimeError, } from './system-info-adapter.js';
export { createBrowserFeedbackWxApis, WEFAT_FEEDBACK_API_NAMES, WEFAT_FEEDBACK_STYLES, WxFeedbackRuntimeError, } from './feedback-adapter.js';
export class WxFacadeRuntimeError extends Error {
    code;
    remediation;
    constructor(code, message, remediation) {
        super(`${code} ${message}`);
        this.name = 'WxFacadeRuntimeError';
        this.code = code;
        this.remediation = remediation;
    }
}
const CAPABILITY_REGISTRIES = new WeakSet();
const UNSAFE_API_SEGMENTS = new Set([
    '__proto__',
    'arguments',
    'caller',
    'constructor',
    'length',
    'name',
    'prototype',
    'then',
]);
function validIdentifierSegment(segment) {
    if (segment.length === 0 || UNSAFE_API_SEGMENTS.has(segment)) {
        return false;
    }
    const first = segment.codePointAt(0);
    if (first === undefined || !(first === 36
        || first === 95
        || (first >= 65 && first <= 90)
        || (first >= 97 && first <= 122))) {
        return false;
    }
    for (let index = 1; index < segment.length; index += 1) {
        const code = segment.codePointAt(index);
        if (code === undefined || !(code === 36
            || code === 95
            || (code >= 48 && code <= 57)
            || (code >= 65 && code <= 90)
            || (code >= 97 && code <= 122))) {
            return false;
        }
    }
    return true;
}
function validateApiName(api) {
    if (api.length === 0
        || api.length > 160
        || api.trim() !== api
        || !api.split('.').every(validIdentifierSegment)) {
        throw new WxFacadeRuntimeError('WF6101', `Invalid wx API registration name: ${api || '<empty>'}`, 'Register a dot-separated static wx API path using safe JavaScript identifier segments.');
    }
}
function validateImplementation(api, implementation) {
    if (typeof implementation !== 'function') {
        throw new WxFacadeRuntimeError('WF6101', `wx.${api} registration must provide a callable implementation`, 'Register a function that implements the capability or omit the unavailable registration.');
    }
}
export function createWxCapabilityRegistry() {
    const implementations = new Map();
    let sealed = false;
    const registry = {
        get apis() {
            return Object.freeze([...implementations.keys()].sort());
        },
        get sealed() {
            return sealed;
        },
        register(api, implementation) {
            validateApiName(api);
            validateImplementation(api, implementation);
            if (sealed) {
                throw new WxFacadeRuntimeError('WF6104', `Cannot register wx.${api} after the capability registry is sealed`, 'Register every configured browser adapter and provider before evaluating application scripts.');
            }
            if (implementations.has(api)) {
                throw new WxFacadeRuntimeError('WF6102', `Duplicate wx API registration: wx.${api}`, 'Keep one deterministic implementation for each wx API; provider registration is not last-write-wins.');
            }
            implementations.set(api, implementation);
        },
        resolve(api) {
            validateApiName(api);
            return implementations.get(api) ?? null;
        },
        seal() {
            sealed = true;
        },
    };
    CAPABILITY_REGISTRIES.add(registry);
    return Object.freeze(registry);
}
function readOnlyError(path) {
    return new WxFacadeRuntimeError('WF6104', `The scoped wx facade is read-only at wx.${path}`, 'Configure capability implementations through the generated provider module before application startup.');
}
function unavailableError(path) {
    return new WxFacadeRuntimeError('WF6103', `wx.${path} has no configured browser implementation`, 'Configure the required adapter or provider, or select an explicit product fallback before building for production.');
}
export function createWxFacade(registry) {
    if (registry === null || typeof registry !== 'object' || !CAPABILITY_REGISTRIES.has(registry)) {
        throw new WxFacadeRuntimeError('WF6101', 'A wx facade requires a valid capability registry', 'Create the registry with createWxCapabilityRegistry and pass it directly to createWxFacade.');
    }
    const members = new Map();
    const immutableHandler = (path) => ({
        defineProperty() {
            throw readOnlyError(path);
        },
        deleteProperty() {
            throw readOnlyError(path);
        },
        set() {
            throw readOnlyError(path);
        },
        setPrototypeOf() {
            throw readOnlyError(path);
        },
    });
    function member(path) {
        const cached = members.get(path);
        if (cached !== undefined) {
            return cached;
        }
        const target = Object.freeze(function wxApiMember(...args) {
            const implementation = registry.resolve(path);
            if (implementation === null) {
                throw unavailableError(path);
            }
            return Reflect.apply(implementation, undefined, args);
        });
        const proxy = new Proxy(target, {
            ...immutableHandler(path),
            get(functionTarget, property) {
                if (property === Symbol.toStringTag) {
                    return 'WefatWxApi';
                }
                if (typeof property === 'symbol') {
                    return undefined;
                }
                if (property === 'then') {
                    return undefined;
                }
                const descriptor = Reflect.getOwnPropertyDescriptor(functionTarget, property);
                if (descriptor !== undefined && descriptor.configurable === false) {
                    return Reflect.get(functionTarget, property);
                }
                if (property in Function.prototype) {
                    return Reflect.get(functionTarget, property);
                }
                return member(`${path}.${String(property)}`);
            },
        });
        members.set(path, proxy);
        return proxy;
    }
    const target = Object.freeze(Object.create(null));
    return new Proxy(target, {
        ...immutableHandler('<root>'),
        get(_target, property) {
            if (property === Symbol.toStringTag) {
                return 'WefatWxFacade';
            }
            if (typeof property === 'symbol') {
                return undefined;
            }
            if (property === 'then') {
                return undefined;
            }
            return member(String(property));
        },
    });
}
//# sourceMappingURL=index.js.map